import {
    AvailabilityStatus,
    ListingFreshness,
    ListingFreshnessStatus,
    OptionAvailability,
    PriceSnapshot,
    PriceSnapshotDailyLowest,
    PriceSnapshotRangeSummary,
    PriceSnapshotRangeWindowDays,
    SourceStatus
} from './domain';
import { calculatePublicEffectivePrice, dateKeyFromPriceTimestamp } from './price-utils';

export type PriceSnapshotRangeDays = PriceSnapshotRangeWindowDays;

export type PriceSnapshotWriteInput = Partial<PriceSnapshot> & {
    salePrice?: number | string | null;
    sale_price?: number | string | null;
};

export interface PriceSnapshotRangeQuery {
    productClusterId?: string;
    listingIds?: string[];
    days?: PriceSnapshotRangeDays;
    startAt?: string;
    endAt?: string;
    includeOutOfStock?: boolean;
    includeUntrusted?: boolean;
}

export interface LowestPriceByRangeResult {
    snapshot: PriceSnapshot;
    effectivePrice: number;
    checkedAt: string;
    collectedAt: string;
    public_effective_price: number;
    checked_at: string;
    collected_at: string;
}

export interface PriceSnapshotRepository {
    saveSnapshot(snapshot: PriceSnapshotWriteInput): Promise<PriceSnapshot>;
    getSnapshotsByRange(query: PriceSnapshotRangeQuery): Promise<PriceSnapshot[]>;
    getLowestPriceByRange(query: PriceSnapshotRangeQuery): Promise<LowestPriceByRangeResult | null>;
    getPriceTrendByRange(query: PriceSnapshotRangeQuery): Promise<PriceSnapshotRangeSummary>;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const COMPARABLE_STOCK_STATUSES: AvailabilityStatus[] = ['in_stock', 'low_stock'];
const CURRENT_LOWEST_FRESHNESS_STATUSES: ListingFreshnessStatus[] = ['fresh', 'stale'];

type RawSnapshotInput = Record<string, unknown>;

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function hasOwn(input: RawSnapshotInput, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(input, key);
}

function numberValue(value: unknown): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
        const parsed = Number(value.replace(/,/g, '').trim());
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function positiveAmount(value: unknown): number {
    const parsed = numberValue(value);
    if (parsed === null) return 0;
    return Math.max(0, Math.round(parsed));
}

function firstNumber(input: RawSnapshotInput, keys: string[]): number | null {
    for (const key of keys) {
        const parsed = numberValue(input[key]);
        if (parsed !== null) return parsed;
    }
    return null;
}

function firstString(input: RawSnapshotInput, keys: string[]): string | null {
    for (const key of keys) {
        const value = input[key];
        if (typeof value === 'string' && value.trim()) return value;
    }
    return null;
}

function firstBoolean(input: RawSnapshotInput, keys: string[]): boolean | null {
    for (const key of keys) {
        const value = input[key];
        if (typeof value === 'boolean') return value;
    }
    return null;
}

function firstOptionAvailability(input: RawSnapshotInput): OptionAvailability[] {
    const camelValue = input.optionAvailability;
    if (Array.isArray(camelValue)) return cloneData(camelValue as OptionAvailability[]);

    const snakeValue = input.option_availability;
    if (Array.isArray(snakeValue)) return cloneData(snakeValue as OptionAvailability[]);

    return [];
}

function firstListingFreshness(input: RawSnapshotInput): ListingFreshness | undefined {
    const camelValue = input.freshness;
    if (camelValue && typeof camelValue === 'object') return cloneData(camelValue as ListingFreshness);

    const snakeValue = input.listing_freshness;
    if (snakeValue && typeof snakeValue === 'object') return cloneData(snakeValue as ListingFreshness);

    return undefined;
}

function timestamp(value: string): number {
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : NaN;
}

function snapshotFreshnessStatus(snapshot: PriceSnapshot): ListingFreshnessStatus | null {
    return snapshot.freshness?.status || snapshot.listing_freshness?.status || null;
}

function isTrustedCurrentSnapshot(snapshot: PriceSnapshot): boolean {
    const freshnessStatus = snapshotFreshnessStatus(snapshot);
    return !freshnessStatus || CURRENT_LOWEST_FRESHNESS_STATUSES.includes(freshnessStatus);
}

function isComparableSnapshot(snapshot: PriceSnapshot, includeUntrusted = false): boolean {
    return COMPARABLE_STOCK_STATUSES.includes(snapshot.stockStatus) && (includeUntrusted || isTrustedCurrentSnapshot(snapshot));
}

function snapshotTime(snapshot: PriceSnapshot): number {
    return timestamp(snapshot.checkedAt);
}

function sortSnapshotsByCheckedAt(a: PriceSnapshot, b: PriceSnapshot): number {
    const timeDiff = snapshotTime(a) - snapshotTime(b);
    if (timeDiff !== 0) return timeDiff;
    return a.effectivePrice - b.effectivePrice;
}

function sortSnapshotsByLowest(a: PriceSnapshot, b: PriceSnapshot): number {
    const priceDiff = a.effectivePrice - b.effectivePrice;
    if (priceDiff !== 0) return priceDiff;
    return snapshotTime(b) - snapshotTime(a);
}

function toLowestPriceResult(snapshot: PriceSnapshot): LowestPriceByRangeResult {
    return {
        snapshot: cloneData(snapshot),
        effectivePrice: snapshot.effectivePrice,
        checkedAt: snapshot.checkedAt,
        collectedAt: snapshot.collectedAt,
        public_effective_price: snapshot.effectivePrice,
        checked_at: snapshot.checkedAt,
        collected_at: snapshot.collectedAt
    };
}

function latestSnapshotByCheckedAt(snapshots: PriceSnapshot[]): PriceSnapshot | null {
    return [...snapshots]
        .filter((snapshot) => Number.isFinite(snapshotTime(snapshot)))
        .sort((a, b) => snapshotTime(b) - snapshotTime(a))[0] || null;
}

export function normalizePriceSnapshot(input: PriceSnapshotWriteInput): PriceSnapshot {
    const raw = input as RawSnapshotInput;
    const listingId = firstString(raw, ['listingId', 'listing_id']);
    const productClusterId = firstString(raw, ['productClusterId', 'product_cluster_id']);

    if (!listingId) throw new Error('PriceSnapshot listingId is required');
    if (!productClusterId) throw new Error('PriceSnapshot productClusterId is required');

    const checkedAt = firstString(raw, ['checkedAt', 'checked_at']) || new Date().toISOString();
    const collectedAt = firstString(raw, ['collectedAt', 'collected_at']) || checkedAt;
    const sourceStatus = (firstString(raw, ['sourceStatus', 'source_status']) || 'mock') as SourceStatus;
    const stockStatus = (firstString(raw, ['stockStatus', 'availability_status']) || 'checking') as AvailabilityStatus;
    const basePrice = positiveAmount(firstNumber(raw, ['basePrice', 'listed_price']));
    const shippingFeeInput = positiveAmount(firstNumber(raw, ['shippingFee', 'shipping_fee']));
    const freeShippingCondition = firstString(raw, ['freeShippingCondition', 'free_shipping_condition']);
    const explicitEffectivePrice = firstNumber(raw, ['effectivePrice', 'public_effective_price']);
    const hasDiscountBreakdown = [
        'instantDiscount',
        'instant_discount',
        'publicCoupon',
        'public_coupon_discount',
        'salePrice',
        'sale_price'
    ].some((key) => hasOwn(raw, key));
    let instantDiscount = positiveAmount(firstNumber(raw, ['instantDiscount', 'instant_discount']));
    let publicCoupon = positiveAmount(firstNumber(raw, ['publicCoupon', 'public_coupon_discount']));

    if (explicitEffectivePrice !== null && !hasDiscountBreakdown) {
        const impliedDiscount = Math.max(0, Math.round(basePrice + shippingFeeInput - explicitEffectivePrice));
        if (firstBoolean(raw, ['couponApplied']) === true) {
            publicCoupon = impliedDiscount;
        } else {
            instantDiscount = impliedDiscount;
        }
    }

    const effectivePrice = calculatePublicEffectivePrice({
        basePrice,
        instantDiscount,
        publicCoupon,
        shippingFee: shippingFeeInput,
        freeShippingThreshold: null,
        free_shipping_condition: freeShippingCondition,
        stockStatus
    });
    const shippingFee = Math.max(0, effectivePrice - Math.max(0, basePrice - instantDiscount - publicCoupon));
    const couponApplied = firstBoolean(raw, ['couponApplied']) ?? publicCoupon > 0;
    const variantId = firstString(raw, ['variantId', 'variant_id']);
    const optionAvailability = firstOptionAvailability(raw);
    const freshness = firstListingFreshness(raw);
    const id = firstString(raw, ['id']) || `snapshot-${listingId}-${checkedAt.replace(/\D/g, '')}`;

    return {
        id,
        listingId,
        productClusterId,
        effectivePrice,
        basePrice,
        shippingFee,
        couponApplied,
        stockStatus,
        collectedAt,
        checkedAt,
        sourceStatus,
        variantId,
        instantDiscount,
        publicCoupon,
        freeShippingCondition,
        optionAvailability,
        freshness,

        product_cluster_id: productClusterId,
        listing_id: listingId,
        variant_id: variantId,
        checked_at: checkedAt,
        collected_at: collectedAt,
        listed_price: basePrice,
        instant_discount: instantDiscount,
        public_coupon_discount: publicCoupon,
        shipping_fee: shippingFee,
        free_shipping_condition: freeShippingCondition,
        public_effective_price: effectivePrice,
        availability_status: stockStatus,
        option_availability: optionAvailability,
        listing_freshness: freshness,
        source_status: sourceStatus
    };
}

export class InMemoryPriceSnapshotRepository implements PriceSnapshotRepository {
    private snapshots = new Map<string, PriceSnapshot>();

    constructor(initialSnapshots: PriceSnapshotWriteInput[] = []) {
        initialSnapshots.forEach((snapshot) => {
            const normalized = normalizePriceSnapshot(snapshot);
            this.snapshots.set(normalized.id, normalized);
        });
    }

    async saveSnapshot(snapshot: PriceSnapshotWriteInput): Promise<PriceSnapshot> {
        const normalized = normalizePriceSnapshot(snapshot);
        this.snapshots.set(normalized.id, normalized);
        return cloneData(normalized);
    }

    async getSnapshotsByRange(query: PriceSnapshotRangeQuery = {}): Promise<PriceSnapshot[]> {
        const scopedSnapshots = this.scopedSnapshots(query);
        if (scopedSnapshots.length === 0) return [];

        const endTime = this.rangeEndTime(query, scopedSnapshots);
        if (endTime === null) return [];

        const startTime = this.rangeStartTime(query, endTime);
        const rangedSnapshots = scopedSnapshots
            .filter((snapshot) => {
                if (query.includeOutOfStock === false && !COMPARABLE_STOCK_STATUSES.includes(snapshot.stockStatus)) return false;
                const checkedTime = snapshotTime(snapshot);
                return Number.isFinite(checkedTime) && checkedTime >= startTime && checkedTime <= endTime;
            })
            .sort(sortSnapshotsByCheckedAt);

        return cloneData(rangedSnapshots);
    }

    async getLowestPriceByRange(query: PriceSnapshotRangeQuery = {}): Promise<LowestPriceByRangeResult | null> {
        const snapshots = await this.getSnapshotsByRange({
            ...query,
            includeOutOfStock: query.includeOutOfStock ?? false
        });
        const comparableSnapshots = snapshots.filter((snapshot) => isComparableSnapshot(snapshot, query.includeUntrusted ?? false));
        const lowestSnapshot = comparableSnapshots.sort(sortSnapshotsByLowest)[0];

        if (!lowestSnapshot) return null;

        return toLowestPriceResult(lowestSnapshot);
    }

    async getPriceTrendByRange(query: PriceSnapshotRangeQuery = {}): Promise<PriceSnapshotRangeSummary> {
        const snapshots = await this.getSnapshotsByRange({
            ...query,
            includeOutOfStock: query.includeOutOfStock ?? true
        });
        const comparableSnapshots = snapshots.filter((snapshot) => {
            if (query.includeOutOfStock === false && !COMPARABLE_STOCK_STATUSES.includes(snapshot.stockStatus)) return false;
            return isComparableSnapshot(snapshot, query.includeUntrusted ?? false);
        });
        const dailyLowest = this.dailyLowestSnapshots(comparableSnapshots);
        const overallLowest = await this.getLowestPriceByRange(query);
        const latestSnapshot = latestSnapshotByCheckedAt(snapshots);
        const firstSnapshot = [...snapshots]
            .filter((snapshot) => Number.isFinite(snapshotTime(snapshot)))
            .sort(sortSnapshotsByCheckedAt)[0] || null;
        const startAt = query.startAt || firstSnapshot?.checkedAt || null;
        const endAt = query.endAt || latestSnapshot?.checkedAt || null;

        return {
            productClusterId: query.productClusterId || null,
            listingIds: query.listingIds ? [...query.listingIds] : null,
            days: query.days || null,
            startAt,
            endAt,
            checkedAtBasis: 'checked_at',
            snapshots: cloneData(snapshots),
            dailyLowest,
            overallLowest,
            latestCheckedAt: latestSnapshot?.checkedAt || null,
            latestCollectedAt: latestSnapshot?.collectedAt || null,
            snapshotCount: snapshots.length,
            comparableSnapshotCount: comparableSnapshots.length,
            hasData: dailyLowest.length > 0,

            product_cluster_id: query.productClusterId || null,
            listing_ids: query.listingIds ? [...query.listingIds] : null,
            start_at: startAt,
            end_at: endAt,
            checked_at_basis: 'checked_at',
            daily_lowest: dailyLowest,
            overall_lowest: overallLowest,
            latest_checked_at: latestSnapshot?.checkedAt || null,
            latest_collected_at: latestSnapshot?.collectedAt || null,
            snapshot_count: snapshots.length,
            comparable_snapshot_count: comparableSnapshots.length,
            has_data: dailyLowest.length > 0
        };
    }

    private scopedSnapshots(query: PriceSnapshotRangeQuery): PriceSnapshot[] {
        const listingIds = query.listingIds ? new Set(query.listingIds) : null;

        return Array.from(this.snapshots.values()).filter((snapshot) => {
            if (query.productClusterId && snapshot.productClusterId !== query.productClusterId) return false;
            if (listingIds && !listingIds.has(snapshot.listingId)) return false;
            return true;
        });
    }

    private rangeEndTime(query: PriceSnapshotRangeQuery, snapshots: PriceSnapshot[]): number | null {
        if (query.endAt) {
            const parsed = timestamp(query.endAt);
            return Number.isFinite(parsed) ? parsed : null;
        }

        const times = snapshots.map(snapshotTime).filter((time) => Number.isFinite(time));
        if (times.length === 0) return null;

        return Math.max(...times);
    }

    private rangeStartTime(query: PriceSnapshotRangeQuery, endTime: number): number {
        if (query.startAt) {
            const parsed = timestamp(query.startAt);
            if (Number.isFinite(parsed)) return parsed;
        }

        if (query.days) return endTime - ((query.days - 1) * DAY_MS);
        return Number.NEGATIVE_INFINITY;
    }

    private dailyLowestSnapshots(snapshots: PriceSnapshot[]): PriceSnapshotDailyLowest[] {
        const grouped = new Map<string, PriceSnapshot[]>();

        snapshots.forEach((snapshot) => {
            const dateKey = dateKeyFromPriceTimestamp(snapshot.checkedAt);
            const dailySnapshots = grouped.get(dateKey) || [];
            dailySnapshots.push(snapshot);
            grouped.set(dateKey, dailySnapshots);
        });

        return Array.from(grouped.keys()).sort().map((dateKey) => {
            const dailySnapshots = grouped.get(dateKey) || [];
            const lowestSnapshot = [...dailySnapshots].sort(sortSnapshotsByLowest)[0];

            return {
                dateKey,
                snapshot: cloneData(lowestSnapshot),
                sourceCount: dailySnapshots.length,
                effectivePrice: lowestSnapshot.effectivePrice,
                checkedAt: lowestSnapshot.checkedAt,
                collectedAt: lowestSnapshot.collectedAt,

                date_key: dateKey,
                source_count: dailySnapshots.length,
                public_effective_price: lowestSnapshot.effectivePrice,
                checked_at: lowestSnapshot.checkedAt,
                collected_at: lowestSnapshot.collectedAt
            };
        });
    }
}

export function createInMemoryPriceSnapshotRepository(initialSnapshots: PriceSnapshotWriteInput[] = []): InMemoryPriceSnapshotRepository {
    return new InMemoryPriceSnapshotRepository(initialSnapshots);
}
